import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../config";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Shield, ArrowRight, Play, ThumbsUp } from "lucide-react";

const getConfidenceLevel = (score) => {
  if (score >= 70) return { label: "HIGH", color: "text-emerald-600 bg-emerald-50 border-emerald-200" };
  if (score >= 55) return { label: "MODERATE", color: "text-blue-600 bg-blue-50 border-blue-200" };
  if (score >= 40) return { label: "BUILDING", color: "text-amber-600 bg-amber-50 border-amber-200" };
  return { label: "LOW", color: "text-slate-500 bg-slate-50 border-slate-200" };
};

const getBarColor = (pct) => {
  if (pct >= 70) return "#1D9E75";
  if (pct >= 50) return "#D97706";
  return "#DC2626";
};

export const SeeWhatWorks = () => {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products?limit=3&sort=trust_score`)
      .then(r => {
        const p = (r.data.products || r.data).filter(
          pr => pr.zcs?.display_ready && pr.zcs?.zcs !== null && pr.zcs?.breakdown?.effectiveness?.length > 0
        );
        setProducts(p.slice(0, 3));
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  if (loaded && products.length === 0) return null;

  return (
    <section className="py-20 px-4 bg-white" data-testid="see-what-works-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3 px-4 py-1.5 bg-[#5B6BA8]/[0.06] rounded-full">
            <ThumbsUp className="w-4 h-4 text-[#5B6BA8]" />
            <span className="text-sm font-medium text-[#5B6BA8]">Product Reviews & Votes</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-semibold mb-3 text-gradient-brand">How Well These Products Worked for Real Users</h2>
          <p className="text-muted-foreground text-sm sm:text-base max-w-lg mx-auto">
            Votes from Community reveal what helped — and what didn't.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {products.map((p) => {
            const score = p.zcs?.zcs || 0;
            const confidence = getConfidenceLevel(score);
            const claims = p.zcs?.breakdown?.effectiveness || [];
            const totalVotes = p.zcs?.total_votes || 0;
            const videoCount = p.video_count || 0;

            return (
              <Link
                to={`/product/${p.slug || p.product_id}`}
                key={p.product_id}
                className="group"
              >
                <Card
                  className="h-full border border-slate-100 hover:border-[#5B6BA8]/25 hover:shadow-lg transition-all rounded-2xl overflow-hidden"
                  data-testid={`works-card-${p.product_id}`}
                >
                  <CardContent className="p-5">
                    {/* Product header */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-slate-50 shrink-0 border border-slate-100">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0 flex-1">
                        {p.brand && (
                          <p className="text-[10px] font-semibold text-[#5B6BA8] uppercase tracking-wide">{p.brand}</p>
                        )}
                        <h3 className="text-sm font-semibold text-foreground line-clamp-2 leading-snug">{p.name}</h3>
                      </div>
                      {/* CCS Circle */}
                      <div
                        className="w-12 h-12 rounded-full shrink-0 flex items-center justify-center shadow-sm"
                        style={{ background: 'linear-gradient(135deg, #3B5AC6 0%, #6B4FA8 50%, #B06BA8 100%)' }}
                        data-testid={`ccs-circle-${p.product_id}`}
                      >
                        <span className="text-sm font-extrabold text-white leading-none">{Math.round(score)}%</span>
                      </div>
                    </div>

                    {/* Confidence badge */}
                    <div className="flex items-center gap-2 mb-5">
                      <span className="text-sm text-[#5B6BA8] font-bold">Community Confidence</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${confidence.color}`}>
                        {confidence.label}
                      </span>
                    </div>

                    {/* Claim bars */}
                    <div className="space-y-3 mb-5">
                      {claims.slice(0, 3).map((claim, i) => (
                        <div key={claim.claim_id || claim.text || `claim-${i}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground truncate mr-2">{claim.text}</span>
                            <span className="text-xs font-bold text-foreground shrink-0">{claim.percentage}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${Math.min(claim.percentage, 100)}%`, backgroundColor: getBarColor(claim.percentage) }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Footer stats */}
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <span className="text-xs text-muted-foreground">
                        {totalVotes} votes{videoCount > 0 ? ` \u00B7 ${videoCount} video review${videoCount > 1 ? "s" : ""}` : ""}
                      </span>
                      <span className="text-xs font-medium text-[#5B6BA8] flex items-center gap-1">
                        View Reviews <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
